/*
 * Opening hours as the branch editor holds them: every day of the week with its ranges
 * ("08:00"-"23:00"), and dates the branch is closed. The stored shape is the server's
 * `storeHoursSchema`; "24:00" closes a range at midnight.
 */

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Day = (typeof DAYS)[number];
export type TimeRange = [string, string];

export interface StoreHours {
	weekly: Partial<Record<Day, TimeRange[]>>;
	closed: string[];
}

export const DAY_LABEL: Record<Day, string> = {
	mon: "Monday",
	tue: "Tuesday",
	wed: "Wednesday",
	thu: "Thursday",
	fri: "Friday",
	sat: "Saturday",
	sun: "Sunday",
};

/** Ranges a day can hold in the editor: a split shift and no more. */
export const MAX_RANGES = 2;

/** What "Set opening hours" starts from. */
export function defaultHours(): {
	weekly: Record<Day, TimeRange[]>;
	closed: string[];
} {
	return {
		weekly: Object.fromEntries(
			DAYS.map((day) => [day, [["08:00", "23:00"]] as TimeRange[]]),
		) as Record<Day, TimeRange[]>,
		closed: [],
	};
}

/** Every day present (closed days empty), as the server's schema wants it. */
export function completeHours(hours: StoreHours): {
	weekly: Record<Day, TimeRange[]>;
	closed: string[];
} {
	return {
		weekly: Object.fromEntries(
			DAYS.map((day) => [day, hours.weekly[day] ?? []]),
		) as Record<Day, TimeRange[]>,
		closed: [...hours.closed].sort(),
	};
}

const minutes = (time: string) => {
	const [h, m] = time.split(":").map(Number);
	return (h ?? 0) * 60 + (m ?? 0);
};

/** The first thing wrong with the hours, in words, or null. */
export function hoursProblem(hours: StoreHours, today: string): string | null {
	for (const day of DAYS) {
		const ranges = [...(hours.weekly[day] ?? [])].sort(
			(a, b) => minutes(a[0]) - minutes(b[0]),
		);
		for (const [index, [start, end]] of ranges.entries()) {
			if (!start || !end) {
				return `${DAY_LABEL[day]}: fill in both times`;
			}
			if (minutes(end) <= minutes(start)) {
				return `${DAY_LABEL[day]}: closing time must be after opening time`;
			}
			const next = ranges[index + 1];
			if (next && minutes(next[0]) < minutes(end)) {
				return `${DAY_LABEL[day]}: the two ranges overlap`;
			}
		}
	}
	const past = hours.closed.find((date) => date < today);
	return past ? `${past} has already passed` : null;
}

/** "08:00-23:00", "Closed", "08:00-14:00, 16:00-23:00". */
export function rangesLabel(ranges: readonly TimeRange[] | undefined): string {
	return ranges && ranges.length > 0
		? ranges.map(([start, end]) => `${start}-${end}`).join(", ")
		: "Closed";
}

/** "Mon-Sun 08:00-23:00", or each run of days that share hours. */
export function hoursSummary(hours: StoreHours | null): string {
	if (!hours) {
		return "No hours set";
	}
	const short = (day: Day) => DAY_LABEL[day].slice(0, 3);
	const runs: { from: Day; to: Day; label: string }[] = [];
	for (const day of DAYS) {
		const label = rangesLabel(hours.weekly[day]);
		const last = runs[runs.length - 1];
		if (last && last.label === label) {
			last.to = day;
		} else {
			runs.push({ from: day, to: day, label });
		}
	}
	return runs
		.map(
			({ from, to, label }) =>
				`${from === to ? short(from) : `${short(from)}-${short(to)}`} ${label}`,
		)
		.join(" · ");
}
